import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  LogOut,
  ArrowLeft,
  Shield,
  Receipt,
  CreditCard,
  Settings
} from "lucide-react";

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { to: "/admin/users", icon: Users, label: "Utilisateurs" },
    { to: "/admin/organizations", icon: Building2, label: "Établissements" },
    { to: "/admin/subscriptions", icon: CreditCard, label: "Abonnements" },
    { to: "/admin/invoices", icon: Receipt, label: "Factures" },
    { to: "/admin/settings", icon: Settings, label: "Paramètres" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-900 to-slate-800 text-white p-6 hidden md:flex flex-col z-40">
        {/* Logo */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">
              Admin
            </h1>
          </div>
          <p className="text-slate-400 text-sm">Panneau d'administration</p>
        </div>

        {/* Back to App */}
        <NavLink
          to="/dashboard"
          className="flex items-center gap-3 px-4 py-3 mb-4 bg-white/5 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Retour à l'app</span>
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-amber-400">Administrateur</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-slate-900 flex items-center justify-between px-4 md:hidden z-40">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-400" />
          <h1 className="text-lg font-bold text-white">Admin</h1>
        </div>
        <button
          onClick={handleLogout}
          className="p-2 text-slate-400 hover:text-white"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Main Content */}
      <main className="md:ml-72 pt-16 md:pt-0 min-h-screen">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900 flex justify-around items-center py-3 px-4 md:hidden z-40 pb-safe">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-all ${
                isActive
                  ? "text-amber-400 bg-amber-400/10"
                  : "text-slate-400"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default AdminLayout;
